import React, { useRef, useState } from 'react';

import classNames from 'classnames';
import AddNotedIcon from 'icons/add-notes.svg';
import DownloadIcon from 'icons/ic-download.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import useModal from 'components/hooks/useModal';
import useOutsideClick from 'components/hooks/useOutsideClick';

import AdditionalTrainingTable from 'components/mobileTrainingProfile/components/homeScreen/AdditionalTrainingTable';
import DownloadReportModalContent from 'components/mobileTrainingProfile/components/homeScreen/DownloadReportModalContent';
import RoleTrainingTable from 'components/mobileTrainingProfile/components/homeScreen/RoleTrainingTable';
import TrainingRecordsTabStatus from 'components/mobileTrainingProfile/components/homeScreen/TrainingRecordsTabStatus';
import Modal from 'components/mobileTrainingProfile/components/Modal';
import { indexResources } from 'components/mobileTrainingProfile/helpers/requests';

import DocumentGeneratorService from 'components/helpers/DocumentGeneratorService';
import { resourceShape } from 'components/helpers/serialisableResources';

export default function TrainingRecordsTab({
  attachmentsGroupedByTrainingId,
  bookings,
  bookingsIndexedByCourseId,
  companyRole,
  companyRoles,
  courseCompanyRoles,
  courses,
  isTrainingEvidenceProcessed,
  onViewCourseDetailsClick,
  personnelId,
  registrationsIndexedByCourseId,
  training,
  trainingGroupedByCourseId,
  trainingStatus,
  userCourses,
  userCoursesIndexedByCourseId,
}) {
  const generator = useRef(null);
  const reportMenuRef = useRef();

  const [isModalOpen, _setIsModallOpen, openModal, closeModal] =
    useModal(false);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isReportError, setIsReportError] = useState(false);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'mobileTrainingProfile.components.homeScreen.TrainingRecordsTab',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const tabCompanyRoleCourseIds =
    companyRole ? companyRole.relationships.courses.data.map((c) => c.id) : [];
  const tabCompanyRoleSpecificCourses = tabCompanyRoleCourseIds.map((id) =>
    courses.find((course) => course.id === id),
  );
  const tabCompanyRoleCourseCompanyRoles = courseCompanyRoles.filter(
    (ccr) => ccr.relationships.companyRole.data.id === companyRole.id,
  );

  const allUserCourseCourseIds = userCourses.map(
    (uc) => uc.relationships.course.data.id,
  );
  const allCompanyRoleCourseIds = companyRoles.reduce((acc, cr) => {
    const ids = cr.relationships.courses.data.map((c) => c.id);
    return [...acc, ...ids];
  }, []);

  const nonAssociatedTraining = training.filter(
    (t) =>
      ![...allUserCourseCourseIds, ...allCompanyRoleCourseIds].includes(
        t.relationships.course.data.id,
      ),
  );
  const nonAssociatedTrainingCourseIds = nonAssociatedTraining.map(
    (t) => t.relationships.course.data.id,
  );
  const bookingCourseIds = bookings.map((b) => b.relationships.course.data.id);

  const additionalCourses = [
    ...new Set([
      ...allUserCourseCourseIds,
      ...allCompanyRoleCourseIds,
      ...nonAssociatedTrainingCourseIds,
      ...bookingCourseIds,
    ]),
  ]
    .filter((id) => !tabCompanyRoleCourseIds.includes(id))
    .map((id) => courses.find((course) => course.id === id));
  const roleHasCourses = tabCompanyRoleSpecificCourses.length > 0;

  const courseCompanyRolesIndexedByCourseId = indexResources({
    resources: tabCompanyRoleCourseCompanyRoles,
    path: 'relationships.course.data.id',
  });

  const successCallback = (docUrl) => {
    closeModal();
    window.location.href = docUrl;
  };

  const failureCallback = () => {
    setIsReportError(true);
  };

  const generateReport = (options = {}) => {
    setIsReportError(false);
    generator.current = new DocumentGeneratorService({
      generateUrl: `/personnel/${personnelId}/training_report_pdfs`,
      generateParams: {
        ...options,
        personnel_id: personnelId,
        company_role_id: companyRole?.id,
      },
      successCallback,
      failureCallback,
    });
    generator.current.start();
  };

  const handleDownloadReportClick = (options) => {
    generator.current?.end();
    generateReport(options);
    openModal();
  };

  useOutsideClick(() => {
    setIsDropdownOpen(false);
  }, [reportMenuRef]);

  const toggleMenu = () => setIsDropdownOpen((prevState) => !prevState);

  if (
    tabCompanyRoleSpecificCourses.length === 0 &&
    additionalCourses.length === 0
  ) {
    return (
      <div className='tw-flex tw-h-90 tw-flex-col tw-items-center tw-justify-center tw-rounded-lg tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-px-4'>
        <AddNotedIcon className='-tw-mt-3' height={92} width={92} />
        <div className='tw-mt-3 tw-text-center tw-text-l tw-font-semibold tw-text-grey-900'>
          {i18nTCommon('training.noTrainingAdded')}
        </div>
        <div className='tw-mt-4 tw-text-center tw-text-grey-700'>
          {i18nTCommon('training.contactManagerToRequestTraining')}
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={classNames(
          'tw-flex tw-h-16 tw-items-center tw-justify-between tw-rounded-tl-lg tw-rounded-tr-lg tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-px-4',
          !roleHasCourses && 'tw-rounded-bl-lg tw-rounded-br-lg',
        )}
      >
        <TrainingRecordsTabStatus trainingStatus={trainingStatus} />
        {isTrainingEvidenceProcessed && (
          <div className='tw-relative' ref={reportMenuRef}>
            <div
              className={classNames(
                'tw-flex tw-h-8 tw-w-8 tw-flex-none tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-1 tw-border-solid hover:tw-border-grey-100',
                isDropdownOpen ? 'tw-border-grey-100' : 'tw-border-transparent',
              )}
              data-element-name='report-menu-toggle'
              onClick={toggleMenu}
            >
              <div className='tw-flex tw-h-1 tw-w-4 tw-justify-between'>
                <div className='tw-h-1 tw-w-1 tw-rounded-full tw-bg-grey-700' />
                <div className='tw-h-1 tw-w-1 tw-rounded-full tw-bg-grey-700' />
                <div className='tw-h-1 tw-w-1 tw-rounded-full tw-bg-grey-700' />
              </div>
            </div>
            {isDropdownOpen && (
              <div
                className='tw-absolute tw-right-0 tw-top-9 tw-z-[9999] tw-w-72 tw-rounded-lg tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-shadow-lg'
                data-element-name='report-menu-dropdown'
              >
                <div
                  className='tw-flex tw-w-72 tw-flex-col'
                  onClick={toggleMenu}
                >
                  <div
                    className='tw-flex tw-h-11 tw-cursor-pointer tw-items-center tw-border-grey-100 tw-px-3 tw-font-medium tw-text-grey-900 hover:tw-bg-blue-025'
                    onClick={() => {
                      handleDownloadReportClick({ evidence: true });
                    }}
                  >
                    <DownloadIcon
                      className='tw-mr-1 [&_polygon]:tw-fill-grey-900'
                      height={24}
                      width={24}
                    />
                    {i18nT('download.withEvidence')}
                  </div>
                  <div
                    className='tw-flex tw-h-11 tw-cursor-pointer tw-items-center tw-border-0 tw-border-t-1 tw-border-solid tw-border-grey-100 tw-px-3 tw-font-medium tw-text-grey-900 hover:tw-bg-blue-025'
                    onClick={() => {
                      handleDownloadReportClick({ evidence: false });
                    }}
                  >
                    <DownloadIcon
                      className='tw-mr-1 [&_polygon]:tw-fill-grey-900'
                      height={24}
                      width={24}
                    />
                    {i18nT('download.withoutEvidence')}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {roleHasCourses && (
        <RoleTrainingTable
          attachmentsGroupedByTrainingId={attachmentsGroupedByTrainingId}
          bookingsIndexedByCourseId={bookingsIndexedByCourseId}
          courseCompanyRolesIndexedByCourseId={
            courseCompanyRolesIndexedByCourseId
          }
          courses={tabCompanyRoleSpecificCourses}
          onViewCourseDetailsClick={onViewCourseDetailsClick}
          registrationsIndexedByCourseId={registrationsIndexedByCourseId}
          trainingGroupedByCourseId={trainingGroupedByCourseId}
          userCoursesIndexedByCourseId={userCoursesIndexedByCourseId}
        />
      )}
      {additionalCourses.length > 0 && (
        <>
          <div
            className={classNames(
              'tw-mt-3 tw-flex tw-h-16 tw-items-center tw-justify-between tw-rounded-tl-lg tw-rounded-tr-lg tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-px-4',
              additionalCourses === 0 && 'tw-rounded-bl-lg tw-rounded-br-lg',
            )}
          >
            <span className='tw-text-l tw-font-semibold'>
              {roleHasCourses || companyRoles.length > 0 ?
                i18nTCommon('training.trainingOutsideThisRole')
              : i18nTCommon('shared.training')}
            </span>
          </div>
          <AdditionalTrainingTable
            attachmentsGroupedByTrainingId={attachmentsGroupedByTrainingId}
            bookingsIndexedByCourseId={bookingsIndexedByCourseId}
            courses={additionalCourses}
            onViewCourseDetailsClick={onViewCourseDetailsClick}
            registrationsIndexedByCourseId={registrationsIndexedByCourseId}
            trainingGroupedByCourseId={trainingGroupedByCourseId}
            userCoursesIndexedByCourseId={userCoursesIndexedByCourseId}
          />
        </>
      )}
      <Modal closeModal={closeModal} isOpen={isModalOpen}>
        <DownloadReportModalContent
          closeModal={closeModal}
          isReportError={isReportError}
        />
      </Modal>
    </>
  );
}

TrainingRecordsTab.propTypes = {
  trainingStatus: PropTypes.string.isRequired,
  isTrainingEvidenceProcessed: PropTypes.bool.isRequired,
  personnelId: PropTypes.string.isRequired,
  companyRole: resourceShape('companyRole'),
  courses: PropTypes.arrayOf(resourceShape('course')).isRequired,
  companyRoles: PropTypes.arrayOf(resourceShape('companyRole')).isRequired,
  training: PropTypes.arrayOf(resourceShape('training')).isRequired,
  bookings: PropTypes.arrayOf(resourceShape('booking')).isRequired,
  userCourses: PropTypes.arrayOf(resourceShape('userCourse')).isRequired,
  courseCompanyRoles: PropTypes.arrayOf(resourceShape('courseCompanyRole'))
    .isRequired,
  userCoursesIndexedByCourseId: PropTypes.object.isRequired,
  bookingsIndexedByCourseId: PropTypes.object.isRequired,
  registrationsIndexedByCourseId: PropTypes.object.isRequired,
  trainingGroupedByCourseId: PropTypes.object.isRequired,
  attachmentsGroupedByTrainingId: PropTypes.object.isRequired,
  onViewCourseDetailsClick: PropTypes.func.isRequired,
};
